import MyPages from "@/components/MyPages";
import FamilySharing from "@/components/FamilySharing";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Star, Wand2 } from "lucide-react";
import PageSVG from "@/components/PageSVG";
import { CATEGORIES, PAGES } from "@/data/registry";
import { sfx } from "@/lib/sounds";

function DiffStars({ n }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3].map((i) => (
        <Star key={i} size={13} className={i <= n ? "fill-pumpkin text-pumpkin" : "text-white/25"} />
      ))}
    </span>
  );
}

function CategorySection({ cat, progress }) {
  const navigate = useNavigate();
  const pages = PAGES.filter((p) => p.category === cat.id);
  const locked = progress.count < cat.unlockAt;
  const remaining = Math.max(0, cat.unlockAt - progress.count);

  return (
    <div className="mb-16" data-testid={`vault-category-${cat.id}`}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-display text-3xl sm:text-4xl" style={{ color: cat.accent }}>
              {cat.name}
            </h3>
            <span
              className="rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-widest"
              style={{ borderColor: `${cat.accent}66`, color: cat.accent }}
            >
              {cat.tag}
            </span>
          </div>
          <p className="mt-1 text-sm text-paper/60">{cat.blurb}</p>
        </div>
        {locked && (
          <div
            data-testid={`vault-lock-hint-${cat.id}`}
            className="flex items-center gap-2 rounded-full border border-pumpkin/40 bg-pumpkin/10 px-4 py-2 text-sm font-medium text-pumpkin"
          >
            <Lock size={15} /> Finish {remaining} more page{remaining > 1 ? "s" : ""} to unlock
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {pages.map((p, idx) => {
          return (
            <motion.div
              key={p.id}
              data-testid={`vault-card-${p.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: idx * 0.06, duration: 0.5 }}
              whileHover={locked ? undefined : { y: -8, rotate: idx % 2 ? 0.6 : -0.6 }}
              onClick={() => {
                if (locked) {
                  sfx.click();
                  return;
                }
                sfx.boop();
                navigate(`/color/${p.id}`);
              }}
              className={`card-edge group relative overflow-hidden p-4 ${locked ? "cursor-not-allowed opacity-90" : "cursor-pointer hover:border-pumpkin/50 hover:shadow-glow-orange"}`}
            >
              <div className="relative overflow-hidden rounded-2xl bg-[#FFFDF8] p-3">
                <div className={locked ? "blur-[7px] saturate-0" : ""}>
                  {p.imagePage ? (
                    <img src={p.image} alt={p.title} className="block w-full" />
                  ) : (
                    <PageSVG page={p} />
                  )}
                </div>
                {locked && (
                  <div
                    data-testid={`vault-lock-${p.id}`}
                    className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-ink/70"
                  >
                    <span className="animate-floaty rounded-full bg-pumpkin p-4 text-ink shadow-glow-orange">
                      <Lock size={26} />
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-widest text-paper/90">
                      Unlocks at {cat.unlockAt} pages
                    </span>
                    <div className="h-2 w-32 overflow-hidden rounded-full bg-white/15">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-pumpkin to-slime transition-all"
                        style={{ width: `${Math.min(100, (progress.count / cat.unlockAt) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-4 flex items-center justify-between px-1">
                <div>
                  <div className="font-semibold text-paper">{p.title}</div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <DiffStars n={p.difficulty} />
                    {progress.completed.includes(p.id) && (
                      <span className="rounded-full bg-slime/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slime">
                        Done
                      </span>
                    )}
                  </div>
                </div>
                {!locked && (
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-pumpkin text-ink transition-transform group-hover:scale-110">
                    <Wand2 size={17} />
                  </span>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export default function Vault({ progress }) {
  return (
    <section id="vault" data-testid="vault-section" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-8">
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        className="font-display text-5xl text-paper sm:text-6xl"
      >
        The Scribble <span className="text-pumpkin text-glow-orange">Vault</span>
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        className="mt-3 max-w-xl text-paper/65"
      >
        Every page you finish counts. Complete 3 to crack open the next secret
        category — heroes, kitties, anime hunters and glam ghouls await.
      </motion.p>
      <MyPages />
      <FamilySharing />
      <div className="mt-12">
        {CATEGORIES.map((cat) => (
          <CategorySection key={cat.id} cat={cat} progress={progress} />
        ))}
      </div>
    </section>
  );
}
